import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import {
    CreatePerformanceTeamReview,
    CreatePerformanceTeamReviewFailure,
    CreatePerformanceTeamReviewSuccess,
    DeletePerformanceTeamReview,
    DeletePerformanceTeamReviewFailure,
    DeletePerformanceTeamReviewSuccess,
    FetchPerformanceTeamMemberOverveview,
    FetchPerformanceTeamMemberOverveviewFailure,
    FetchPerformanceTeamMemberOverveviewSuccess,
    FetchPerformanceTeamReview,
    FetchPerformanceTeamReviewFailure,
    FetchPerformanceTeamReviewSuccess,
    UpdatePerformanceTeamParticipants,
    UpdatePerformanceTeamParticipantsFailure,
    UpdatePerformanceTeamParticipantsSuccess,
    UpdatePerformanceTeamReview,
    UpdatePerformanceTeamReviewFailure,
    UpdatePerformanceTeamReviewSuccess,
} from 'app/library/performance-team-review/actions';
import { IPerformanceTeamReviewActionPayload } from 'app/library/performance-team-review/interfaces/create-performance-team-review.interface';
import { PerformanceTeamMemberOverview } from 'app/library/performance-team-review/models/performance-team-member-overview.model';
import { PerformanceTeamReview } from 'app/library/performance-team-review/models/performance-team-review.model';
import { PerformanceTeamReviewState } from 'app/library/performance-team-review/models/performance-team-review.state';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    FetchTrainingApprovalTeamMemberOverview,
    FetchTrainingApprovalTeamMemberOverviewFailure,
    FetchTrainingApprovalTeamMemberOverviewSuccess,
    FetchTrainingApprovalTeamOverview,
    FetchTrainingApprovalTeamOverviewFailure,
    FetchTrainingApprovalTeamOverviewSuccess,
    FetchTrainingApprovalTeamRequests,
    FetchTrainingApprovalTeamRequestsFailure,
    FetchTrainingApprovalTeamRequestsSuccess,
} from '../actions';

@Injectable()
export class TrainingApprovalTeamRequestsService {
    @Select(PerformanceTeamReviewState.getEntity)
    performanceTeamReview$: Observable<PerformanceTeamReview>;

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getPerformanceTeamReviewById(performanceTeamReviewId: string): PerformanceTeamReview {
        return PerformanceTeamReviewState.getEntity(this._store.selectSnapshot(PerformanceTeamReviewState), performanceTeamReviewId);
    }

    async fetchPerformanceTeamReview(id: string): Promise<boolean> {
        this._store.dispatch(new FetchPerformanceTeamReview(id));

        return await this._actionHelper.race(FetchPerformanceTeamReviewSuccess, FetchPerformanceTeamReviewFailure).toPromise();
    }

    async fetchTrainingApprovalTeamRequests(
        teamId: string,
        search: string,
        pageSize: number,
        pageIndex: number,
        includeSubteams: boolean,
        statusesIds: string[] = [],
        requestDate?: ExtendedAttributeFilterInterface,
        lastUpdate?: ExtendedAttributeFilterInterface,
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        columns?: string[]
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchTrainingApprovalTeamRequests({
                teamId,
                search,
                pageSize,
                pageIndex,
                includeSubteams,
                statusesIds,
                requestDate,
                lastUpdate,
                extendedAttributesFilters,
                columns,
            })
        );

        return await this._actionHelper.race(FetchTrainingApprovalTeamRequestsSuccess, FetchTrainingApprovalTeamRequestsFailure).toPromise();
    }

    async createPerformanceTeamReview(performanceTeamReview: IPerformanceTeamReviewActionPayload): Promise<PerformanceTeamReview> {
        this._store.dispatch(new CreatePerformanceTeamReview(performanceTeamReview));

        return race(
            this._action$.pipe(
                ofActionDispatched(CreatePerformanceTeamReviewSuccess),
                map((action: CreatePerformanceTeamReviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(CreatePerformanceTeamReviewFailure),
                map((action: CreatePerformanceTeamReviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updatePerformanceTeamParticipants(teamReviewId: string, userIds: string[]): Promise<PerformanceTeamReview> {
        this._store.dispatch(new UpdatePerformanceTeamParticipants({ teamReviewId, userIds }));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamParticipantsSuccess),
                map((action: UpdatePerformanceTeamParticipantsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamParticipantsFailure),
                map((action: UpdatePerformanceTeamParticipantsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getTrainingApprovalTeamRequestsOverview(teamId: string, includeSubteams: boolean): Promise<{ inProgressRequests: number }> {
        this._store.dispatch(new FetchTrainingApprovalTeamOverview({ teamId, includeSubteams }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTrainingApprovalTeamOverviewSuccess),
                map((action: FetchTrainingApprovalTeamOverviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTrainingApprovalTeamOverviewFailure),
                map((action: FetchTrainingApprovalTeamOverviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getTrainingApprovalTeamMemberRequestsOverview(teamId: string, userId: string): Promise<{ inProgressRequests: number }> {
        this._store.dispatch(new FetchTrainingApprovalTeamMemberOverview({ teamId, userId }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchTrainingApprovalTeamMemberOverviewSuccess),
                map((action: FetchTrainingApprovalTeamMemberOverviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchTrainingApprovalTeamMemberOverviewFailure),
                map((action: FetchTrainingApprovalTeamMemberOverviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getPerformanceTeamMemberReviewOverview(teamId: string, userId: string): Promise<PerformanceTeamMemberOverview[]> {
        this._store.dispatch(new FetchPerformanceTeamMemberOverveview({ teamId, userId }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamMemberOverveviewSuccess),
                map((action: FetchPerformanceTeamMemberOverveviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamMemberOverveviewFailure),
                map((action: FetchPerformanceTeamMemberOverveviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updatePerformanceTeamReview(performanceTeamReviewId: string, performanceTeamReview: IPerformanceTeamReviewActionPayload): Promise<PerformanceTeamReview> {
        this._store.dispatch(new UpdatePerformanceTeamReview({ performanceTeamReviewId, performanceTeamReview }));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamReviewSuccess),
                map((action: UpdatePerformanceTeamReviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamReviewFailure),
                map((action: UpdatePerformanceTeamReviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async deletePerformanceTeamReview(id: string): Promise<boolean> {
        this._store.dispatch(new DeletePerformanceTeamReview(id));

        return await this._actionHelper.race(DeletePerformanceTeamReviewSuccess, DeletePerformanceTeamReviewFailure).toPromise();
    }
}
